// Decoding a completed form. Meta returns option ids (arrays for checkboxes), booleans for opt-ins
// and "YYYY-MM-DD" for dates; `response_json` arrives as a string or (from the fork) already parsed.
// Answers are decoded with the definition of the version the customer filled in, so titles are the
// ones the customer saw even after the form was edited, and nothing is keyed by position.

import { type FieldComponent, type FieldType, type FormDefinition, fieldsOf } from './definition.ts';
import { FORM_CONSTANT, VERSION_CONSTANT } from './generator.ts';

export type AnswerValue = string | string[] | boolean | null;

export interface Answer {
  fieldId: string;
  screenId: string;
  label: string;
  type: FieldType;
  /** What the customer answered, readable: text, option title(s), true/false; null when blank. */
  value: AnswerValue;
  /** Choice fields: the option ids Meta returned (unknown ids are kept as their own title). */
  optionIds?: string[];
}

export interface DecodedResponse {
  flowToken: string | null;
  formPublicId: string | null;
  version: number | null;
  /** Everything else in the payload, keyed as received. */
  fields: Record<string, unknown>;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Parses `whatsapp_flow_response.response_json` (a JSON string from Meta, an object once the fork
 * parsed it). Returns null when it is not a JSON object.
 */
export function parseResponseJson(raw: unknown): Record<string, unknown> | null {
  if (isObject(raw)) return raw;
  if (typeof raw !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function decodeResponse(payload: Record<string, unknown>): DecodedResponse {
  const { flow_token: token, [FORM_CONSTANT]: form, [VERSION_CONSTANT]: version, ...fields } = payload;
  const versionNumber =
    typeof version === 'number' ? version : typeof version === 'string' ? Number(version) : NaN;
  return {
    flowToken: typeof token === 'string' && token ? token : null,
    formPublicId: typeof form === 'string' && form ? form : null,
    version: Number.isInteger(versionNumber) && versionNumber > 0 ? versionNumber : null,
    fields,
  };
}

const asText = (value: unknown): string | null => {
  if (typeof value === 'string') return value.trim() ? value : null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
};

/** Checkbox values: an array of ids; tolerate a JSON-encoded array or a single id. */
function asIdList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(asText).filter((id): id is string => id !== null);
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      return asIdList(JSON.parse(value));
    } catch {
      return [];
    }
  }
  const single = asText(value);
  return single ? [single] : [];
}

function decodeField(field: FieldComponent, raw: unknown): Pick<Answer, 'value' | 'optionIds'> {
  switch (field.type) {
    case 'dropdown':
    case 'radio':
    case 'checkbox': {
      const ids = field.type === 'checkbox' ? asIdList(raw) : asIdList(raw).slice(0, 1);
      if (ids.length === 0) return { value: null, optionIds: [] };
      const titles = ids.map((id) => field.options.find((option) => option.id === id)?.title.trim() ?? id);
      return { value: field.type === 'checkbox' ? titles : titles[0]!, optionIds: ids };
    }
    case 'opt_in':
      if (raw === true || raw === 'true') return { value: true };
      if (raw === false || raw === 'false') return { value: false };
      return { value: null };
    default:
      return { value: asText(raw) };
  }
}

/** Answers in form order, one per field of the definition (null when not in the payload). */
export function mapAnswers(definition: FormDefinition, fields: Record<string, unknown>): Answer[] {
  return fieldsOf(definition).map(({ screen, field }) => ({
    fieldId: field.id,
    screenId: screen.id,
    label: field.label.trim(),
    type: field.type,
    ...decodeField(field, fields[field.id]),
  }));
}

/**
 * Fields whose answer cannot be accepted: a required field left blank (a required opt-in that is not
 * ticked) or a choice that is not one of the field's options. The WhatsApp client enforces
 * `required` itself; the Flow endpoint checks again so a stale or forged payload is refused.
 */
export function invalidAnswers(
  definition: FormDefinition,
  fields: Record<string, unknown>,
): FieldComponent[] {
  return fieldsOf(definition)
    .filter(({ field }) => {
      const raw = fields[field.id];
      if ('options' in field) {
        const ids = asIdList(raw);
        if (ids.some((id) => !field.options.some((option) => option.id === id))) return true;
        return field.required && ids.length === 0;
      }
      if (field.type === 'opt_in') return field.required && raw !== true && raw !== 'true';
      return field.required && asText(raw) === null;
    })
    .map(({ field }) => field);
}

/** Payload keys that are not fields of the definition (kept in the raw payload). */
export function extraKeys(definition: FormDefinition, fields: Record<string, unknown>): string[] {
  const known = new Set(fieldsOf(definition).map(({ field }) => field.id));
  return Object.keys(fields).filter((key) => !known.has(key));
}

/** One line of text for tables, CSV cells and notes. */
export function formatAnswer(value: AnswerValue): string {
  if (value === null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return value;
}

/**
 * The value saved to a contact attribute: numbers for number inputs, booleans for opt-ins, option
 * titles (comma separated for checkboxes), text otherwise. Undefined when there is nothing to save.
 */
export function attributeValue(field: FieldComponent, answer: Answer): string | number | boolean | undefined {
  if (answer.value === null) return undefined;
  if (field.type === 'opt_in') return answer.value === true;
  if (field.type === 'short_text' && field.inputType === 'number' && typeof answer.value === 'string') {
    const number = Number(answer.value);
    return Number.isFinite(number) ? number : answer.value;
  }
  const text = formatAnswer(answer.value);
  return text === '' ? undefined : text;
}

/** Chatwoot custom attribute type for a field's saved value. */
export function attributeType(field: FieldComponent): 'text' | 'number' | 'date' | 'checkbox' {
  if (field.type === 'opt_in') return 'checkbox';
  if (field.type === 'date') return 'date';
  if (field.type === 'short_text' && field.inputType === 'number') return 'number';
  return 'text';
}

/** Labels to add to the contact for the chosen options. */
export function labelsFor(definition: FormDefinition, answers: readonly Answer[]): string[] {
  const labels = new Set<string>();
  for (const { field } of fieldsOf(definition)) {
    if (!('options' in field)) continue;
    const chosen = answers.find((answer) => answer.fieldId === field.id)?.optionIds ?? [];
    for (const option of field.options) {
      const label = option.addLabel?.trim().toLowerCase();
      if (label && chosen.includes(option.id)) labels.add(label);
    }
  }
  return [...labels];
}

const MARKDOWN_SPECIAL = /([\\`*_[\]#>|~])/g;

/** The private note posted in the conversation: form, version and every answer. */
export function noteText(formName: string, version: number | null, answers: readonly Answer[]): string {
  const escape = (value: string) => value.replace(MARKDOWN_SPECIAL, '\\$1');
  const lines = answers.map(
    (answer) =>
      `- **${escape(answer.label || answer.fieldId)}:** ${answer.value === null ? '-' : escape(formatAnswer(answer.value))}`,
  );
  const heading = `**Form submitted: ${escape(formName)}**${version ? ` (version ${version})` : ''}`;
  return [heading, '', ...lines].join('\n');
}
